// src/components/doctor/DoctorDocumentsSection.jsx
//
// Read-only list of every document a patient has uploaded — closes the gap
// found while testing Sprint 15: DoctorPatientDetailPage only ever showed
// the AI-generated triage report (via DoctorTriageView), with no way for a
// doctor to see the patient's raw uploaded files — prescriptions, medical
// bills, scans, or even the original blood test PDF the AI report was
// generated from.
//
// Deliberately read-only — no upload/edit/delete — unlike the navigator's
// HealthRecordsTab.js, which has those capabilities and was deliberately
// NOT reused elsewhere for that exact reason (see PatientUploadPage.jsx's
// own comment: "Does NOT reuse navigator HealthRecordsTab — different UX,
// simpler, no delete/edit").
//
// Reuses, doesn't reinvent:
//   - healthRecordAPI.list() / getDownloadUrl() — same endpoints already
//     proven working on both the navigator and patient sides this sprint.
//     Already IDOR-guarded server-side; DOCTOR role passes
//     isSelfOrStaff(patientId, authentication) regardless of patientId,
//     same as every other doctor-facing patient-scoped call in this app.
//   - RECORD_TYPES / getTypeConfig from constants/recordTypes.js — the
//     same single source of truth PatientRecordsPage.jsx and
//     HealthRecordsTab.js already use.
//
// Styled to match this file's siblings in the doctor portal (inline hex +
// shared .btn classes from index.css, per DoctorPatientDetailPage.jsx's
// own established local convention) rather than the patient portal's
// CSS-variable-only rule, since this component only ever renders inside
// the doctor portal.

import React, { useState, useEffect, useCallback } from 'react';
import { healthRecordAPI } from '../../services/api';
import { RECORD_TYPES, getTypeConfig } from '../../constants/recordTypes';
import { useToast } from '../../context/ToastContext';
import HeldReportNotice from '../HeldReportNotice';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function chipStyle(active) {
  return {
    padding: '5px 12px', borderRadius: 20,
    border: `1px solid ${active ? 'var(--brand-dark)' : 'var(--border)'}`,
    background: active ? 'var(--brand-dark)' : 'var(--bg-surface)',
    color: active ? 'var(--bg-surface)' : 'var(--text-secondary)',
    fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  };
}

export default function DoctorDocumentsSection({ patientId }) {
  const { toast } = useToast();

  const [records, setRecords]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [filterType, setFilterType]       = useState('ALL');
  const [busyId, setBusyId]               = useState(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    healthRecordAPI.list(patientId)
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.content || []);
        const sorted = [...data].sort(
          (a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0)
        );
        setRecords(sorted);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load documents for this patient.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [patientId]);

  /**
   * DEF-026 / TC-D005 — "No View option on any document."
   *
   * The only action was Download, which for a PDF means leaving the patient's
   * record to open a file from the downloads folder. A doctor reading through a
   * patient's documents wants to look at them, not collect them.
   *
   * Both actions use the same presigned URL from getDownloadUrl(); the
   * difference is only whether the browser is asked to render it in a tab or
   * save it. So View adds no new endpoint and no new access path — the same
   * server-side IDOR guard applies to both.
   */
  const openRecord = useCallback(async (record, mode) => {
    if (!patientId) return;
    setBusyId(`${record.id}:${mode}`);
    try {
      const res = await healthRecordAPI.getDownloadUrl(patientId, record.id);
      const url = res.data.downloadUrl;

      if (mode === 'view') {
        const win = window.open(url, '_blank', 'noopener');
        if (!win) {
          toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
        }
        return;
      }

      // Download: an <a download> rather than window.open, so the browser saves
      // the file instead of rendering it. window.open on a PDF opens the viewer,
      // which made the old "Download" button behave like a View — part of why
      // the absence of a real View action was not obvious.
      const link = document.createElement('a');
      link.href = url;
      link.download = record.fileName || 'document';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.error(mode === 'view'
        ? 'Could not open this document. Please try again.'
        : 'Could not generate a download link. Please try again.');
    } finally {
      setBusyId(null);
    }
  }, [patientId, toast]);

  if (loading) {
    return <p className="field-text">Loading documents…</p>;
  }

  if (error) {
    return <p className="field-text" style={{ color: 'var(--red)' }}>{error}</p>;
  }

  if (records.length === 0) {
    return <p className="field-text">No documents uploaded yet.</p>;
  }

  const filtered = filterType === 'ALL'
    ? records
    : records.filter(r => r.recordType === filterType);

  const typesPresent = RECORD_TYPES.filter(t => records.some(r => r.recordType === t.value));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => setFilterType('ALL')}
          style={chipStyle(filterType === 'ALL')}
        >
          All ({records.length})
        </button>
        {typesPresent.map(t => (
          <button
            key={t.value}
            type="button"
            onClick={() => setFilterType(t.value)}
            style={chipStyle(filterType === t.value)}
          >
            {t.icon} {t.label} ({records.filter(r => r.recordType === t.value).length})
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(record => {
          const typeConfig = getTypeConfig(record.recordType);
          return (
            <div
              key={record.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                border: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: 20, flexShrink: 0 }} aria-hidden="true">
                {typeConfig.icon}
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {record.fileName}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {typeConfig.label}
                  {/* DEF-026: "All 10 documents show Blood Test type only."
                      Every upload path was sending a hardcoded BLOOD_TEST, so the
                      stored type carried no information — and nothing on screen
                      distinguished a type someone chose from one nobody checked.
                      recordTypeInferred (server-side, RecordTypeInferencer) makes
                      the difference visible. */}
                  {record.recordTypeInferred && (
                    <span title="Type detected automatically from the document — not confirmed by a person"
                      style={{ marginLeft: 5, fontSize: 10.5, opacity: 0.75 }}>
                      (auto-detected)
                    </span>
                  )}
                  {' · '}{formatDate(record.drawDate || record.uploadedAt)}
                  {record.fileSizeBytes ? ` · ${formatSize(record.fileSizeBytes)}` : ''}
                  {record.uploadedByName ? ` · Uploaded by ${record.uploadedByName}` : ''}
                </div>
                <HeldReportNotice record={record} />
                {/* V108 — read-only here; retrying is a navigator/admin action (HealthRecordsTab.js) */}
                {record.processingStatus === 'FAILED' && (
                  <div style={{ marginTop: 3, fontSize: 11, fontWeight: 600, color: 'var(--red)' }}>
                    Triage failed{record.triageFailureReason ? `: ${record.triageFailureReason}` : ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {/* DEF-026: View is new. Offered for every record — the browser
                    decides what it can render, and a type we cannot preview
                    simply downloads, which is what Download does anyway. Gating
                    View on a mime-type allowlist would hide it for records whose
                    mimeType was never captured. */}
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => openRecord(record, 'view')}
                  disabled={busyId === `${record.id}:view`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  {busyId === `${record.id}:view` ? 'Opening…' : 'View'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => openRecord(record, 'download')}
                  disabled={busyId === `${record.id}:download`}
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  {busyId === `${record.id}:download` ? 'Preparing…' : '⬇ Download'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
